import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Link } from 'react-router-dom';
import { useSites } from '../../context/SiteContext';
import { useReportVisits } from '../../hooks/useReportVisits';
import {
  buildHourly,
  buildKeywords,
  buildOverview,
  buildPages,
  buildSources,
  buildTrend,
  onlineCount,
} from '../../lib/analytics';
import { formatDateTime, formatDuration, formatNumber, formatPercent, startOfDay } from '../../lib/format';
import { classifySource, sourceColor } from '../../lib/source';
import { parseSearch } from '../../lib/parse';
import { visitPagePath } from '../../lib/source';
import type { SourceChannel } from '../../lib/types';
import EmptyState from '../../components/dashboard/EmptyState';
import PageHeader from '../../components/dashboard/PageHeader';
import StatTable from '../../components/dashboard/StatTable';

function formatKpi(value: number, flags?: { isDuration?: boolean; isPercent?: boolean; isDecimal?: boolean }) {
  if (flags?.isDuration) return formatDuration(value);
  if (flags?.isPercent) return formatPercent(value);
  if (flags?.isDecimal) return value.toFixed(1);
  return formatNumber(value);
}

export default function DashboardPage() {
  const { currentSite } = useSites();
  const { humans, ranged, range } = useReportVisits(currentSite);

  if (!currentSite) {
    return (
      <EmptyState
        title="先添加一个站点"
        desc="添加站点并安装统计代码后，这里会展示爱站风格的综合报告。"
      />
    );
  }

  const overview = buildOverview(humans, range.bounds);
  const spanDays = Math.round((range.bounds.to.getTime() - range.bounds.from.getTime()) / 86_400_000);
  const trendDays = spanDays > 7 ? Math.min(30, Math.max(spanDays, 7)) : 7;
  const trend = buildTrend(humans, trendDays);
  const todayHourly = buildHourly(humans, startOfDay());
  const yday = startOfDay();
  yday.setDate(yday.getDate() - 1);
  const yesterdayHourly = buildHourly(humans, yday);
  const hourly = todayHourly.map((h, i) => ({
    label: h.label,
    今日: h.pv,
    昨日: yesterdayHourly[i]?.pv || 0,
  }));
  const sources = buildSources(ranged);
  const keywords = buildKeywords(ranged).slice(0, 8);
  const pages = buildPages(ranged).slice(0, 8);
  const latest = ranged.slice(0, 8);
  const sourceTotal = sources.reduce((sum, s) => sum + s.value, 0) || 1;

  const compareRows = [
    [
      '浏览量(PV)',
      overview.current.pv,
      overview.previous.pv,
      overview.isToday ? overview.predictedPv : '—',
      overview.isToday ? overview.weekAvg.pv : overview.periodAvg.pv,
    ],
    ['独立访客(UV)', overview.current.uv, overview.previous.uv, '—', overview.isToday ? overview.weekAvg.uv : overview.periodAvg.uv],
    ['独立IP', overview.current.ip, overview.previous.ip, '—', overview.isToday ? overview.weekAvg.ip : overview.periodAvg.ip],
    ['新访客', overview.current.newUv, overview.previous.newUv, '—', '—'],
    ['回访客', overview.current.returnUv, overview.previous.returnUv, '—', '—'],
    ['跳出率', formatPercent(overview.current.bounceRate), formatPercent(overview.previous.bounceRate), '—', '—'],
    ['人均浏览', overview.current.pagesPerUv.toFixed(1), overview.previous.pagesPerUv.toFixed(1), '—', '—'],
    ['平均停留', formatDuration(overview.current.avgDuration), formatDuration(overview.previous.avgDuration), '—', '—'],
  ].map((row) => row.map((cell) => (typeof cell === 'number' ? formatNumber(cell) : cell)));
  const compareColumns = overview.isToday
    ? ['指标', '今日', '昨日', '预计今日', '近7日日均']
    : ['指标', '本期', '上期', '—', `日均(${overview.periodDays}天)`];

  return (
    <div>
      <PageHeader
        title="综合报告"
        desc={`${currentSite.name} · ${currentSite.domain} · 当前在线 ${onlineCount(humans)} 人`}
        range={range}
        extra={
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 text-sm text-foreground-600">
              <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse" />
              实时刷新
            </span>
            <button
              type="button"
              onClick={() => {
                const header = overview.isToday
                  ? '指标,今日,昨日,预计今日,近7日日均\n'
                  : `指标,本期,上期,—,日均(${overview.periodDays}天)\n`;
                const lines = [
                  `PV,${overview.current.pv},${overview.previous.pv},${overview.isToday ? overview.predictedPv : '—'},${overview.isToday ? overview.weekAvg.pv : overview.periodAvg.pv}`,
                  `UV,${overview.current.uv},${overview.previous.uv},—,${overview.isToday ? overview.weekAvg.uv : overview.periodAvg.uv}`,
                  `IP,${overview.current.ip},${overview.previous.ip},—,${overview.isToday ? overview.weekAvg.ip : overview.periodAvg.ip}`,
                  `新访客,${overview.current.newUv},${overview.previous.newUv},—,—`,
                  `回访客,${overview.current.returnUv},${overview.previous.returnUv},—,—`,
                ];
                const blob = new Blob([header + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `statflow-${currentSite.domain}-report.csv`;
                a.click();
              }}
              className="px-3 py-1.5 text-xs font-semibold border border-background-300 rounded-md"
            >
              导出报表
            </button>
          </div>
        }
      />

      <div className="mt-6 grid grid-cols-2 xl:grid-cols-4 gap-3">
        {overview.kpis.map((k) => (
          <div key={k.key} className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground-500">{k.label}</span>
              <i className={`${k.icon} text-sm text-primary-600`} />
            </div>
            <div className="mt-2 font-heading text-2xl font-bold text-foreground-950">
              {formatKpi(k.value, k)}
            </div>
            <div className={`mt-1 text-xs font-semibold ${k.delta.up ? 'text-accent-700' : 'text-primary-700'}`}>
              {k.delta.text} {overview.compareLabel}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
        <div className="px-5 py-3 border-b border-background-200">
          <h2 className="text-sm font-semibold text-foreground-900">{overview.isToday ? '今日对比' : '本期对比'}</h2>
        </div>
        <StatTable
          columns={compareColumns}
          rows={compareRows}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground-900">今日分时趋势</h2>
            <span className="text-xs text-foreground-500">对比昨日</span>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={hourly}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={2} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} width={32} />
                <Tooltip />
                <Area type="monotone" dataKey="今日" stroke="#E25A3C" fill="#E25A3C33" />
                <Area type="monotone" dataKey="昨日" stroke="#9A8B7A" fill="#9A8B7A22" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="text-sm font-semibold text-foreground-900">来源构成</h2>
          <div className="mt-5 flex flex-col gap-4">
            {sources.map((s) => {
              const pct = Math.round((s.value / sourceTotal) * 100);
              return (
                <div key={s.name}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-foreground-700">{s.name}</span>
                    <span className="font-semibold text-foreground-900">
                      {pct}% · {formatNumber(s.value)}
                    </span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-background-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${sourceColor(s.name as SourceChannel)}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground-900">
            {trendDays > 7 ? `近 ${trendDays} 日` : '近 7 日'}访问趋势
          </h2>
          <span className="text-xs text-foreground-500">PV / UV</span>
        </div>
        <div className="mt-4 h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend}>
              <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} width={32} />
              <Tooltip />
              <Area type="monotone" dataKey="pv" stroke="#E25A3C" fill="#E25A3C33" name="浏览量" />
              <Area type="monotone" dataKey="uv" stroke="#2F9D6A" fill="#2F9D6A22" name="访客" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground-900">热门搜索词</h2>
            <Link to="/dashboard/keywords" className="text-xs text-primary-600">
              查看全部
            </Link>
          </div>
          <StatTable
            columns={['搜索词', '搜索引擎', '浏览量', '访客']}
            rows={keywords.map((k) => [k.keyword, k.engine, formatNumber(k.pv), formatNumber(k.uv)])}
            empty="当前时段暂无搜索词"
          />
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground-900">受访页面</h2>
            <Link to="/dashboard/pages" className="text-xs text-primary-600">
              查看全部
            </Link>
          </div>
          <StatTable
            columns={['页面', '浏览量', '入口', '出口']}
            rows={pages.map((p) => [p.path, formatNumber(p.pv), formatNumber(p.entry), formatNumber(p.exit)])}
            empty="当前时段暂无页面数据"
          />
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
        <div className="px-5 py-3 border-b border-background-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground-900">最新访客</h2>
          <Link to="/dashboard/realtime" className="text-xs text-primary-600">
            实时访客
          </Link>
        </div>
        <StatTable
          columns={['时间', 'IP', '地域', '来源', '搜索词', '受访页']}
          rows={latest.map((v) => {
            const search = parseSearch(v.referrer, v.pageUrl);
            return [
              formatDateTime(v.createdAt),
              v.ip,
              [v.province, v.city].filter(Boolean).join(' ') || v.city,
              search.engine || classifySource(v.referrer, v.pageUrl),
              search.keyword || '—',
              visitPagePath(v.pageUrl),
            ];
          })}
          empty="暂无访客"
        />
      </div>
    </div>
  );
}
